import cloneDeep from 'clone-deep'
import { daysOfWeek, InputType, monthOption, monthsInYear as _monthsInYear } from 'config'
import { useEffect, useMemo, useState } from 'react'
import { Button } from 'stories/components'
import { formatDate, InputValidate, isEmpty } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import {
  defaultFrequencyInputs,
  hourOptions,
  minuteOptions,
  monthCustomOptionInputs,
  RepeatType,
  WeekOrdOfMonths,
  yearCustomOptionInputs,
} from './constants'

export interface IFrequencyProps {
  data?: Record<string, any>
  startDate?: string
  errorCheck?: boolean
  onChange: Function
  notRequiredFields?: string[]
  repeatTypeToggle?: boolean
  showTime?: boolean
}

export interface ICustomOption {
  weekDays?: string[]
  monthType?: 'date' | 'day'
  dateOfMonth?: string
  weekOrdOfMonth?: string
  weekDayOfMonth?: string
  yearType?: 'date' | 'day'
  monthOfDateYear?: string
  dateOfDateYear?: string
  weekOrdOfYear?: string
  weekDayOfYear?: string
  monthOfDayYear?: string
}
export const Frequency = ({
  data,
  startDate,
  errorCheck = false,
  onChange: _onChange,
  notRequiredFields = [],
  repeatTypeToggle = false,
  showTime = false,
}: IFrequencyProps) => {
  const [inputs, setInputs] = useState<Record<string, InputType>>(defaultFrequencyInputs())
  const [customData, setCustomData] = useState<ICustomOption>({})

  const monthsInYear = cloneDeep(_monthsInYear)
  delete monthsInYear[0]

  useEffect(() => {
    const temp = cloneDeep(inputs)
    temp.startDate.value = startDate
    if (data)
      Object.keys(data).map((key) => {
        if (!inputs[key]) return
        temp[key].value = data[key]
      })
    setInputs(temp)
    setCustomData(data?.custom || {})
  }, [data, startDate])

  useEffect(() => {
    if (!errorCheck) return
    const temp = cloneDeep(inputs)
    Object.keys(inputs).map((key) => {
      temp[key].error = InputValidate(temp[key])
    })
    setInputs(temp)
  }, [errorCheck])

  const inputData = useMemo(() => {
    const json: Record<string, any> = {}
    Object.keys(inputs).map((key) => {
      json[key] = inputs[key].value
    })
    return json
  }, [inputs])

  const onChange = (key: string, value: string) => {
    const temp = cloneDeep(inputs)
    temp[key].value = value
    if (errorCheck && !notRequiredFields.includes(key)) temp[key].error = InputValidate(temp[key])
    setInputs(temp)

    const json: Record<string, any> = {}
    Object.keys(temp).map((key) => {
      json[key] = temp[key].value
    })
    _onChange({ ...json, custom: customData })
  }

  const renderCustomOption = () => {
    const intervalUnit = inputs.intervalUnit.value
    if (inputs.repeatType.value === RepeatType.NoRepeat) return
    if (intervalUnit === 'week') return renderWeekOption()
    if (intervalUnit === 'month') return renderMonthOption()
    if (intervalUnit === 'year') return renderYearOption()
    return
  }

  const renderWeekOption = () => {
    const onWeekOptionChange = (value: any) => {
      const temp = cloneDeep(customData)
      if (!temp.weekDays) temp.weekDays = []
      let selectedDays = temp.weekDays

      if (!selectedDays.includes(value)) selectedDays.push(value)
      else if (selectedDays.length > 1) selectedDays = selectedDays.filter((v: any) => v !== value)
      temp.weekDays = selectedDays
      setCustomData(temp)

      const json: Record<string, any> = { ...inputData, custom: temp }
      _onChange(json)
    }
    return (
      <>
        <div className="col-span-full flex gap-2">
          {Object.keys(daysOfWeek).map((key: any) => {
            return (
              <Button
                pills
                outline={!customData.weekDays?.includes(key)}
                noPadding
                key={key}
                className="w-8 h-8 flex justify-center items-center px-0 py-0"
                onClick={() => {
                  onWeekOptionChange(key)
                }}
              >
                {daysOfWeek[key][0]}
              </Button>
            )
          })}
        </div>
        {errorCheck && isEmpty(customData.weekDays) && (
          <span className="col-span-full text-red-500 text-sm">Required Field</span>
        )}
      </>
    )
  }

  const renderMonthOption = () => {
    const onMonthOptionChange = (key: string, value: any) => {
      if (key === 'dateOfMonth' && value > 31) return
      const temp = cloneDeep(customData) as any
      temp[key] = value
      setCustomData(temp)
      const json: Record<string, any> = {
        ...inputData,
        custom: temp,
      }
      _onChange(json)
    }

    return (
      <>
        {Object.keys(monthCustomOptionInputs()).map((key: string) => {
          const input: InputType = monthCustomOptionInputs()[key]
          input.value = (customData as any)[key]
          if (customData.monthType === 'date' && key === 'dateOfMonth') input.visible = true
          if (customData.monthType === 'day' && ['weekOrdOfMonth', 'weekDayOfMonth'].includes(key)) input.visible = true

          if (!input.visible) return
          if (errorCheck) input.error = InputValidate(input)

          return (
            <div className={`col-span-${input.span}`} key={key}>
              <RenderInput input={input} key={key} Key={key} onChange={onMonthOptionChange} />
            </div>
          )
        })}
      </>
    )
  }

  const renderYearOption = () => {
    const onYearOptionChange = (key: string, value: any) => {
      if (key === 'dateOfDateYear' && value > 31) return
      const temp = cloneDeep(customData) as any
      temp[key] = value
      setCustomData(temp)
      const json: Record<string, any> = {
        ...inputData,
        custom: temp,
      }
      _onChange(json)
    }
    return (
      <>
        {Object.keys(yearCustomOptionInputs()).map((key: string) => {
          const input: InputType = yearCustomOptionInputs()[key]
          input.value = (customData as any)[key]
          if (customData.yearType === 'date' && ['monthOfDateYear', 'dateOfDateYear'].includes(key))
            input.visible = true
          if (customData.yearType === 'day' && ['weekOrdOfYear', 'weekDayOfYear', 'monthOfDayYear'].includes(key))
            input.visible = true

          if (!input.visible) return
          if (errorCheck) input.error = InputValidate(input)

          return (
            <div className={`col-span-${input.span}`} key={key}>
              <RenderInput input={input} key={key} Key={key} onChange={onYearOptionChange} />
            </div>
          )
        })}
      </>
    )
  }

  const renderDescription = useMemo(() => {
    const repeatEvery = inputs.repeatEvery.value
    const intervalUnit = inputs.intervalUnit.value
    const startDate = inputs.startDate.value
    const endDate = inputs.endDate.value
    const hour = inputs.hour.value
    const minute = inputs.minute.value

    const {
      weekDays,
      monthType,
      dateOfMonth,
      weekOrdOfMonth,
      weekDayOfMonth,
      yearType,
      dateOfDateYear,
      monthOfDateYear,
      weekOrdOfYear,
      weekDayOfYear,
      monthOfDayYear,
    } = customData
    let description = 'Occurs every'
    if (repeatEvery > 1) description = `${description} ${repeatEvery}`

    if (intervalUnit != 'week') description = `${description} ${intervalUnit}${repeatEvery > 1 ? 's' : ''}`

    if (intervalUnit == 'week') {
      if (repeatEvery > 1) description = `${description} weeks on`
      description = `${description} ${
        weekDays
          ?.sort()
          .map((v: any) => daysOfWeek[v])
          .join(', ') || ''
      }`
    } else if (intervalUnit == 'month') {
      description = `${description} on`
      if (monthType == 'date') {
        description = `${description} day ${dateOfMonth || ''}`
      } else if (monthType == 'day') {
        description = `${description} ${weekOrdOfMonth ? WeekOrdOfMonths[weekOrdOfMonth].toLowerCase() : ''} ${
          weekDayOfMonth ? daysOfWeek[Number(weekDayOfMonth)] : ''
        }`
      }
    } else if (intervalUnit == 'year') {
      description = `${description} on`
      if (yearType == 'date') {
        description = `${description} day ${dateOfDateYear || ''} of ${
          monthOfDateYear ? monthOption[Number(monthOfDateYear)] : ''
        }`
      } else if (yearType == 'day') {
        description = `${description} ${weekOrdOfYear ? WeekOrdOfMonths[weekOrdOfYear].toLowerCase() : ''} ${
          weekDayOfYear ? daysOfWeek[Number(weekDayOfYear)] : ''
        } of ${monthOfDayYear ? monthOption[Number(monthOfDayYear)] : ''}`
      }
    }

    description = `${description} starting ${startDate ? formatDate(startDate) : ''}`
    if (endDate) description = `${description} until ${endDate ? formatDate(endDate) : ''}`
    if (showTime && hour && minute) description = `${description} at ${hourOptions[hour]}:${minuteOptions[minute]}`
    return <p className="text-sm italic">{description}</p>
  }, [inputs, customData])

  return (
    <div className="grid grid-cols-4 gap-2">
      {Object.keys(inputs).map((key) => {
        const input = inputs[key]
        if (key === 'repeatType' && repeatTypeToggle) {
          return (
            <div key={key} className={`col-span-${input.span || 1}`}>
              <RenderInput
                input={{
                  inputType: 'toggle',
                  title: 'Off / On',
                  value: input.value === RepeatType.Repeat,
                }}
                onChange={(key: string, value: any) => onChange(key, value ? RepeatType.Repeat : RepeatType.NoRepeat)}
                key={key}
                Key={key}
              />
            </div>
          )
        }
        if (!showTime && ['hour', 'minute'].includes(key)) return
        if (inputs.repeatType.value === RepeatType.NoRepeat && key !== 'repeatType') return
        if (notRequiredFields.includes(key)) input.required = false
        return (
          <div key={key} className={`col-span-${input.span || 1}`}>
            <RenderInput input={input} onChange={onChange} key={key} Key={key} />
          </div>
        )
      })}
      {renderCustomOption()}
      {inputs.repeatType.value !== RepeatType.NoRepeat && <div className="col-span-full">{renderDescription}</div>}
    </div>
  )
}
